import brandLogoUrl from "../img/magallanes-logo.png";
import { withBase, stripBase } from "./services.js";
import { parseLocalePath, getAlternateUrl, localePath, LOCALES } from "../i18n/index.js";
import { SERVICE_ROUTE_CATALOG } from "./route-catalog.js";

const GROWTH_CANONICAL_PATHS = new Set([
  "/",
  ...SERVICE_ROUTE_CATALOG.map((route) => route.canonical),
]);

const MEGA_CONTENT_REVEAL_DELAY_MS = 220;
let closeMenuExternal = null;
let teardownMenuInteractions = null;
const MENU_COMPACT_TOP_MARGIN_PX = 16;

function resolveAccentValue(variableName) {
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
  return value || "#000000";
}

function getCurrentPath() {
  return stripBase(window.location.pathname || "/");
}

function buildMenuMarkup(locale) {
  const teamLabel = locale === "en" ? "TEAM" : "EQUIPO";
  const contactLabel = locale === "en" ? "CONTACT" : "CONTACTO";
  const growthLabel = locale === "en" ? "BUSINESS GROWTH" : "CRECIMIENTO EMPRESARIAL";
  const menuLabel = locale === "en" ? "MENU" : "MENÚ";
  const megaAriaLabel = locale === "en" ? "Areas of work" : "Areas de trabajo";

  return `
    <a class="site-brand" href="${withBase(`/${locale}/`)}" aria-label="Magallanes">
      <img class="site-brand__logo" src="${brandLogoUrl}" alt="Magallanes Business Consulting" />
    </a>
    <nav class="site-menu" data-site-menu data-menu-locale="${locale}" aria-label="${locale === "en" ? "Main menu" : "Menu principal"}">
      <div class="site-menu__mega-overlay" aria-hidden="true"></div>
      <button class="site-menu__trigger" type="button" aria-expanded="false">${menuLabel}</button>
      <div class="site-menu__panel">
        <div class="site-menu__row">
          <div class="site-menu__growth" data-site-growth>
            <button
              class="site-menu__primary"
              data-site-growth-link
              type="button"
              aria-haspopup="true"
              aria-expanded="false"
              aria-controls="site-growth-mega"
            >
              ${growthLabel}
            </button>
            <div
              class="site-menu__mega"
              id="site-growth-mega"
              aria-label="${megaAriaLabel}"
              aria-hidden="true"
            >
              ${SERVICE_ROUTE_CATALOG.map((route) => {
                  const service = route.serviceData;
                  const lbl = locale === "en" ? service.labelEn : service.label;
                  return `<a href="${withBase(localePath(locale, route.canonical))}" data-transition-link data-transition-color="${resolveAccentValue(service.accentVar)}" data-transition-image="${service.imageUrl}">${lbl}</a>`;
                }
              ).join("")}
            </div>
          </div>
          <a class="site-menu__primary" data-site-primary="equipo" href="${withBase(localePath(locale, "/equipo"))}">${teamLabel}</a>
          <a class="site-menu__primary" data-site-primary="contacto" href="${withBase(localePath(locale, "/contacto"))}">${contactLabel}</a>
          <div class="site-lang-switcher">
            ${LOCALES.filter((loc) => loc !== locale).map((loc) => `<a class="site-lang-switcher__btn" data-lang-btn="${loc}" href="${withBase(localePath(loc, "/"))}" hreflang="${loc}" aria-label="${loc === "en" ? "Switch to English" : "Cambiar a español"}">${loc.toUpperCase()}</a>`).join("")}
          </div>
        </div>
      </div>
    </nav>
  `;
}

export function syncSiteMenuCurrent() {
  const menu = document.querySelector("[data-site-menu]");
  if (!menu) {
    return;
  }

  const currentPath = getCurrentPath();
  const { locale: currentLocale, canonical } = parseLocalePath(currentPath);
  const menuLocale = menu.dataset.menuLocale;

  if (menuLocale && menuLocale !== currentLocale) {
    initSiteMenu({ force: true });
    return;
  }

  const growthLink = menu.querySelector("[data-site-growth-link]");
  const equipoLink = menu.querySelector('[data-site-primary="equipo"]');
  const contactoLink = menu.querySelector('[data-site-primary="contacto"]');

  const growthCurrent = GROWTH_CANONICAL_PATHS.has(canonical);
  const equipoCurrent = canonical === "/equipo";
  const contactoCurrent = canonical === "/contacto";

  growthLink?.classList.toggle("is-current", growthCurrent);
  equipoLink?.classList.toggle("is-current", equipoCurrent);
  contactoLink?.classList.toggle("is-current", contactoCurrent);

  const otherLocale = LOCALES.find((loc) => loc !== currentLocale);
  const btn = menu.querySelector(".site-lang-switcher__btn");
  if (btn && otherLocale) {
    btn.dataset.langBtn = otherLocale;
    btn.href = withBase(getAlternateUrl(currentPath, otherLocale));
    btn.hreflang = otherLocale;
    btn.setAttribute("aria-label", otherLocale === "en" ? "Switch to English" : "Cambiar a español");
    btn.textContent = otherLocale.toUpperCase();
  }
}

export function closeSiteMenu() {
  closeMenuExternal?.();
}

export function initSiteMenu({ force = false } = {}) {
  const existingMenu = document.querySelector("[data-site-menu]");
  const menuNeedsHydration = Boolean(existingMenu) && !teardownMenuInteractions;

  if (existingMenu && !force && !menuNeedsHydration) {
    return;
  }

  if (existingMenu && (force || menuNeedsHydration)) {
    teardownMenuInteractions?.();
    existingMenu.previousElementSibling?.classList.contains("site-brand")
      && existingMenu.previousElementSibling.remove();
    existingMenu.remove();
  }

  const currentPath = getCurrentPath();
  const { locale } = parseLocalePath(currentPath);

  document.body.insertAdjacentHTML("beforeend", buildMenuMarkup(locale));
  syncSiteMenuCurrent();

  const menu = document.querySelector("[data-site-menu]");
  const brand = document.querySelector(".site-brand");
  const trigger = menu?.querySelector(".site-menu__trigger");
  const growth = menu?.querySelector("[data-site-growth]");
  const growthLink = menu?.querySelector("[data-site-growth-link]");
  const mega = menu?.querySelector(".site-menu__mega");
  const megaLinks = menu?.querySelectorAll(".site-menu__mega a") ?? [];

  if (!menu || !trigger || !growth || !growthLink || !mega) {
    return;
  }

  let megaContentTimer = 0;
  let resizeRaf = 0;

  const syncCompactMenu = () => {
    if (!menu || !growth || !mega) {
      return;
    }

    if (window.matchMedia("(max-width: 767px)").matches) {
      menu.classList.remove("is-compact", "is-measuring");
      brand?.classList.remove("site-brand--compact");
      return;
    }

    const wasMenuOpen = menu.classList.contains("is-open");
    const wasMegaOpen = menu.classList.contains("is-mega-open");
    const wasMegaReady = menu.classList.contains("is-mega-ready");
    const wasGrowthOpen = growth.classList.contains("is-open");

    menu.classList.remove("is-compact", "is-measuring");
    brand?.classList.remove("site-brand--compact");

    menu.classList.add("is-measuring", "is-open", "is-mega-open");
    growth.classList.add("is-open");

    const megaTop = mega.getBoundingClientRect().top;
    const shouldCompact = megaTop < MENU_COMPACT_TOP_MARGIN_PX;

    menu.classList.remove("is-measuring", "is-open", "is-mega-open", "is-mega-ready");
    growth.classList.remove("is-open");

    if (wasMenuOpen) {
      menu.classList.add("is-open");
    }

    if (wasMegaOpen) {
      menu.classList.add("is-mega-open");
    }

    if (wasMegaReady) {
      menu.classList.add("is-mega-ready");
    }

    if (wasGrowthOpen) {
      growth.classList.add("is-open");
    }

    menu.classList.toggle("is-compact", shouldCompact);
    brand?.classList.toggle("site-brand--compact", shouldCompact);
  };

  const scheduleCompactSync = () => {
    window.cancelAnimationFrame(resizeRaf);
    resizeRaf = window.requestAnimationFrame(syncCompactMenu);
  };

  const clearMegaContentTimer = () => {
    window.clearTimeout(megaContentTimer);
    megaContentTimer = 0;
  };

  const openMenu = () => {
    menu.classList.add("is-open");
    document.body.classList.add("is-site-menu-open");
    trigger.setAttribute("aria-expanded", "true");
  };

  const syncMegaAccessibility = (isOpen) => {
    growthLink.setAttribute("aria-expanded", String(isOpen));
    mega.setAttribute("aria-hidden", String(!isOpen));
    megaLinks.forEach((link) => {
      link.tabIndex = isOpen ? 0 : -1;
    });
  };

  const closeMenu = () => {
    clearMegaContentTimer();
    menu.classList.remove("is-open");
    menu.classList.remove("is-mega-open");
    menu.classList.remove("is-mega-ready");
    growth.classList.remove("is-open");
    document.body.classList.remove("is-site-menu-open");
    document.body.classList.remove("is-site-mega-open");
    trigger.setAttribute("aria-expanded", "false");
    syncMegaAccessibility(false);
  };
  closeMenuExternal = closeMenu;

  const openMega = () => {
    clearMegaContentTimer();
    menu.classList.add("is-mega-open");
    growth.classList.add("is-open");
    syncMegaAccessibility(true);
    document.body.classList.add("is-site-mega-open");

    megaContentTimer = window.setTimeout(() => {
      menu.classList.add("is-mega-ready");
    }, MEGA_CONTENT_REVEAL_DELAY_MS);
  };

  const closeMega = () => {
    clearMegaContentTimer();
    menu.classList.remove("is-mega-ready");
    menu.classList.remove("is-mega-open");
    growth.classList.remove("is-open");
    syncMegaAccessibility(false);
    document.body.classList.remove("is-site-mega-open");
  };

  const onFocusOut = (event) => {
    if (!menu.contains(event.relatedTarget)) {
      closeMenu();
    }
  };
  menu.addEventListener("focusout", onFocusOut);

  const onTriggerClick = () => {
    if (menu.classList.contains("is-open")) {
      closeMenu();
      return;
    }

    openMenu();
  };
  trigger.addEventListener("click", onTriggerClick);

  const onGrowthClick = () => {
    if (!menu.classList.contains("is-open")) {
      openMenu();
    }

    if (growth.classList.contains("is-open")) {
      closeMega();
      return;
    }

    openMega();
  };
  growthLink.addEventListener("click", onGrowthClick);

  const onDocumentClick = (event) => {
    if (!menu.contains(event.target)) {
      closeMenu();
    }
  };
  document.addEventListener("click", onDocumentClick);

  const onDocumentKeydown = (event) => {
    if (event.key === "Escape") {
      closeMenu();
      trigger.focus();
    }
  };
  document.addEventListener("keydown", onDocumentKeydown);

  syncMegaAccessibility(false);
  scheduleCompactSync();
  window.addEventListener("resize", scheduleCompactSync);

  teardownMenuInteractions = () => {
    window.cancelAnimationFrame(resizeRaf);
    menu.removeEventListener("focusout", onFocusOut);
    trigger.removeEventListener("click", onTriggerClick);
    growthLink.removeEventListener("click", onGrowthClick);
    document.removeEventListener("click", onDocumentClick);
    document.removeEventListener("keydown", onDocumentKeydown);
    window.removeEventListener("resize", scheduleCompactSync);
    closeMenuExternal = null;
    teardownMenuInteractions = null;
  };
}
